import type { LocalesInfoRenderer, SiteLocaleRenderer } from '@wix/thunderbolt-ssr-api'
import type {
	IMultilingualServiceConfig,
	SiteLocale,
	SiteLocaleUrlStructure,
	SiteLocaleVisibility,
} from '@wix/viewer-service-multilingual/definition'

const URL_STRUCTURE_MAP: Record<string, SiteLocaleUrlStructure> = {
	SUBDIRECTORY: 'SUBDIRECTORY',
	SUBDOMAIN: 'SUBDOMAIN',
	QUERY_PARAM: 'QUERY_PARAM',
	Subdirectory: 'SUBDIRECTORY',
	Subdomain: 'SUBDOMAIN',
	QueryParam: 'QUERY_PARAM',
}

const VISIBILITY_MAP: Record<string, SiteLocaleVisibility> = {
	VISIBLE: 'VISIBLE',
	HIDDEN: 'HIDDEN',
	Visible: 'VISIBLE',
	Hidden: 'HIDDEN',
}

const getString = (value: unknown): string | undefined => {
	return typeof value === 'string' && value.length > 0 ? value : undefined
}

const getOptionalString = (value: unknown): string | undefined => {
	return typeof value === 'string' ? value : undefined
}

const getOptionalBoolean = (value: unknown): boolean | undefined => {
	return typeof value === 'boolean' ? value : undefined
}

const getVisibility = (value: unknown): SiteLocaleVisibility | undefined => {
	return typeof value === 'string' ? VISIBILITY_MAP[value] : undefined
}

const getUrlStructure = (value: unknown): SiteLocaleUrlStructure | undefined => {
	return typeof value === 'string' ? URL_STRUCTURE_MAP[value] : undefined
}

const assignOptional = <T extends object, K extends keyof T>(target: T, key: K, value: T[K] | undefined) => {
	if (value !== undefined) {
		target[key] = value
	}
}

const mapLocale = (locale: SiteLocaleRenderer): SiteLocale | undefined => {
	const id = getString(locale.id)
	const languageCode = getString(locale.languageCode)
	const visibility = getVisibility(locale.visibility)

	if (!id || !languageCode || !visibility) {
		return undefined
	}

	const serviceLocale: SiteLocale = {
		id,
		languageCode,
		visibility,
	}

	assignOptional(serviceLocale, 'regionCode', getOptionalString(locale.regionCode))
	assignOptional(serviceLocale, 'primaryLocale', getOptionalBoolean(locale.primaryLocale))
	assignOptional(serviceLocale, 'visitorPrimaryLocale', getOptionalBoolean(locale.visitorPrimaryLocale))
	assignOptional(serviceLocale, 'flag', getOptionalString(locale.flag))
	assignOptional(serviceLocale, 'regionalFormat', getOptionalString(locale.regionalFormat))
	assignOptional(serviceLocale, 'urlStructure', getUrlStructure(locale.urlStructure))
	assignOptional(serviceLocale, 'displayName', getOptionalString(locale.displayName))

	return serviceLocale
}

export const getDefaultConfig = (): IMultilingualServiceConfig => ({
	multilingualEnabled: false,
	locales: [],
})

export const toMultilingualServiceConfig = (
	localesInfo: LocalesInfoRenderer | null | undefined
): IMultilingualServiceConfig => {
	if (!localesInfo) {
		return getDefaultConfig()
	}

	return {
		multilingualEnabled: localesInfo.multilingualEnabled,
		locales: localesInfo.locales.map(mapLocale).filter((locale): locale is SiteLocale => !!locale),
	}
}
